import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Trophy, RefreshCw, AlertCircle, Check } from "lucide-react";

const FormDot = ({ result }) => {
  const styles = {
    W: "bg-green-500 text-white",
    D: "bg-zinc-600 text-white",
    L: "bg-primary-accent text-white",
  };
  return (
    <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold ${styles[result] || "bg-zinc-800 text-zinc-500"}`}>
      {result}
    </span>
  );
};

const StandingsTable = ({ rows, testidPrefix, clZone = 0, relegationZone = 0 }) => {
  const total = rows.length;
  const showElo = rows.some((r) => r.elo != null);
  const zoneClass = (pos) => {
    if (clZone && pos <= clZone) return "border-l-2 border-l-green-500";
    if (relegationZone && pos > total - relegationZone) return "border-l-2 border-l-primary-accent";
    return "border-l-2 border-l-transparent";
  };
  return (
  <table className="w-full">
    <thead>
      <tr className="border-b border-white/5">
        <th className="text-left px-3 py-2 label-tag w-10">#</th>
        <th className="text-left px-3 py-2 label-tag">Team</th>
        <th className="text-right px-2 py-2 label-tag w-10">P</th>
        <th className="text-right px-2 py-2 label-tag w-10">W</th>
        <th className="text-right px-2 py-2 label-tag w-10">D</th>
        <th className="text-right px-2 py-2 label-tag w-10">L</th>
        <th className="text-right px-2 py-2 label-tag w-12">GF</th>
        <th className="text-right px-2 py-2 label-tag w-12">GA</th>
        <th className="text-right px-2 py-2 label-tag w-12">GD</th>
        <th className="text-right px-2 py-2 label-tag w-12">Pts</th>
        {showElo && <th className="text-right px-2 py-2 label-tag w-20">Elo</th>}
        <th className="text-right px-3 py-2 label-tag">Form</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => {
        const t = r.team || {};
        return (
          <tr key={r.team?.id || r.position} className={`border-b border-white/5 hover:bg-white/5 ${zoneClass(r.position)}`} data-testid={`${testidPrefix}-row-${t.id}`}>
            <td className="px-3 py-2.5 font-display text-lg text-zinc-400 tabular-nums">{r.position}</td>
            <td className="px-3 py-2.5">
              <div className="flex items-center gap-2">
                {t.crest && <img src={t.crest} alt="" className="w-5 h-5 object-contain" />}
                <span className="font-display text-base tracking-wider">{t.shortName || t.name}</span>
              </div>
            </td>
            <td className="px-2 py-2.5 text-right text-zinc-300 tabular-nums">{r.playedGames}</td>
            <td className="px-2 py-2.5 text-right text-zinc-300 tabular-nums">{r.won}</td>
            <td className="px-2 py-2.5 text-right text-zinc-300 tabular-nums">{r.draw}</td>
            <td className="px-2 py-2.5 text-right text-zinc-300 tabular-nums">{r.lost}</td>
            <td className="px-2 py-2.5 text-right text-zinc-400 tabular-nums">{r.goalsFor}</td>
            <td className="px-2 py-2.5 text-right text-zinc-400 tabular-nums">{r.goalsAgainst}</td>
            <td className="px-2 py-2.5 text-right text-zinc-300 tabular-nums">{r.goalDifference > 0 ? "+" : ""}{r.goalDifference}</td>
            <td className="px-2 py-2.5 text-right font-display text-xl text-primary-accent">{r.points}</td>
            {showElo && (
              <td className="px-2 py-2.5 text-right tabular-nums" data-testid={`${testidPrefix}-elo-${t.id}`}>
                {r.elo != null ? (
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="font-display text-sm text-zinc-200">{r.elo}</span>
                    {r.eloChange != null && r.eloChange !== 0 && (
                      <span className={`text-[10px] font-display tabular-nums ${r.eloChange > 0 ? "text-green-400" : "text-primary-accent"}`}>
                        {r.eloChange > 0 ? "▲" : "▼"}{Math.abs(r.eloChange)}
                      </span>
                    )}
                  </div>
                ) : <span className="text-zinc-600">—</span>}
              </td>
            )}
            <td className="px-3 py-2.5">
              <div className="flex items-center justify-end gap-1">
                {(r.form ? r.form.split(",") : []).slice(-5).map((f, i) => <FormDot key={i} result={f.trim()} />)}
                {!r.form && <span className="text-xs text-zinc-600">—</span>}
              </div>
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
  );
};

const BestThirdsTable = ({ rows }) => (
  <div className="surface-card overflow-x-auto mt-6" data-testid="best-thirds-table">
    <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
      <h2 className="font-display text-2xl uppercase">Best 3rd-Placed Teams</h2>
      <span className="label-tag">Top 8 advance · As It Stands</span>
    </div>
    <table className="w-full">
      <thead>
        <tr className="border-b border-white/5">
          <th className="text-left px-3 py-2 label-tag w-10">#</th>
          <th className="text-left px-3 py-2 label-tag">Team</th>
          <th className="text-center px-2 py-2 label-tag w-12">Grp</th>
          <th className="text-right px-2 py-2 label-tag w-10">P</th>
          <th className="text-right px-2 py-2 label-tag w-12">GF</th>
          <th className="text-right px-2 py-2 label-tag w-12">GA</th>
          <th className="text-right px-2 py-2 label-tag w-12">GD</th>
          <th className="text-right px-3 py-2 label-tag w-12">Pts</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const t = r.team || {};
          return (
            <tr
              key={t.id || r.rank}
              data-testid={`best-third-row-${t.id}`}
              className={`border-b border-white/5 hover:bg-white/5 ${r.qualified ? "bg-green-500/5" : "opacity-60"}`}
            >
              <td className="px-3 py-2.5 font-display text-lg tabular-nums">
                <span className={r.qualified ? "text-green-400" : "text-zinc-500"}>{r.rank}</span>
              </td>
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  {t.crest && <img src={t.crest} alt="" className="w-5 h-5 object-contain" />}
                  <span className="font-display text-base tracking-wider">{t.shortName || t.name}</span>
                  {r.qualified && <span className="text-[9px] uppercase tracking-widest text-green-400 border border-green-500/40 px-1.5 py-0.5">Q</span>}
                </div>
              </td>
              <td className="px-2 py-2.5 text-center text-zinc-400 font-display tracking-wider">{r.group}</td>
              <td className="px-2 py-2.5 text-right text-zinc-300 tabular-nums">{r.playedGames}</td>
              <td className="px-2 py-2.5 text-right text-zinc-400 tabular-nums">{r.goalsFor}</td>
              <td className="px-2 py-2.5 text-right text-zinc-400 tabular-nums">{r.goalsAgainst}</td>
              <td className="px-2 py-2.5 text-right text-zinc-300 tabular-nums">{r.goalDifference > 0 ? "+" : ""}{r.goalDifference}</td>
              <td className="px-3 py-2.5 text-right font-display text-xl text-primary-accent">{r.points}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <div className="px-4 py-2 text-[11px] text-zinc-500 border-t border-white/5">
      Ranked by points, then goal difference, goals for, goals against. Top 8 qualify for the Round of 32.
    </div>
  </div>
);

export default function Standings() {
  const { active } = useTournament();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [groupTab, setGroupTab] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    if (!active) return;
    const { data } = await api.get(`/tournaments/${active.tournament_id}/standings`);
    setData(data);
    if (data?.format === "grouped" && data.standings?.length) {
      // Always reset to first group when tournament changes; preserve if same tournament
      setGroupTab((prev) => {
        const exists = data.standings.some((s) => s.group === prev);
        return exists ? prev : data.standings[0].group;
      });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [active?.tournament_id]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/admin/sync/${active.tournament_id}/standings`);
      await load();
      toast.success("Standings refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed (check API config)");
    } finally { setRefreshing(false); }
  };

  const generateKnockouts = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/generate-knockouts/${active.tournament_id}`);
      if (data.ok) toast.success(`Knockouts populated — ${data.updated + data.created} R32 fixtures set`);
      else toast.error(data.reason === "group stage not complete" ? "Group stage isn't fully settled yet" : (data.reason || "Couldn't generate"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setGenerating(false); }
  };

  if (!active) return null;

  return (
    <StandingsContent
      data={data}
      groupTab={groupTab}
      setGroupTab={setGroupTab}
      refreshing={refreshing}
      refresh={refresh}
      generating={generating}
      generateKnockouts={generateKnockouts}
      user={user}
    />
  );
}

function StandingsContent({ data, groupTab, setGroupTab, refreshing, refresh, generating, generateKnockouts, user }) {
  const groups = useMemo(
    () => (data?.format === "grouped" ? (data.standings || []).map((s) => s.group).filter(Boolean) : []),
    [data]
  );
  const currentTable = useMemo(() => {
    if (!data?.standings?.length) return null;
    if (data.format === "grouped") {
      return data.standings.find((s) => s.group === groupTab) || data.standings[0];
    }
    return data.standings.find((s) => s.type === "TOTAL") || data.standings[0];
  }, [data, groupTab]);

  const empty = !data || !data.standings?.length;
  const isLeague = data?.format === "league";
  const clZone = isLeague ? (data?.cl_zone || 0) : 0;
  const relegationZone = isLeague ? (data?.relegation_zone || 0) : 0;

  return (
    <div className="px-3 md:px-8 py-6 md:py-8 grid-bg min-h-[calc(100vh-65px)]">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-tag mb-1">Live Table</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase flex items-center gap-3">
            <Trophy size={32} className="text-primary-accent"/> Standings
          </h1>
          {data?.competition_name && (
            <div className="text-sm text-zinc-400 mt-1">{data.competition_name} · {data.season?.startDate?.slice(0, 4)}–{data.season?.endDate?.slice(0, 4)}</div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {data?.synced_at && (
            <div className="text-xs text-zinc-500 uppercase tracking-widest text-right">
              <div>Last synced</div>
              <div className="text-zinc-300 font-display tracking-wider">{new Date(data.synced_at).toLocaleString()}</div>
            </div>
          )}
          {user?.role === "admin" && (
            <>
              {!isLeague && (
                <button
                  data-testid="generate-knockouts-btn"
                  onClick={generateKnockouts}
                  disabled={generating}
                  className="border border-primary-accent text-primary-accent px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-primary-accent hover:text-white disabled:opacity-50"
                >
                  <Trophy size={12} /> {generating ? "Generating…" : "Generate Knockouts"}
                </button>
              )}
              <button
                data-testid="standings-refresh"
                onClick={refresh}
                disabled={refreshing}
                className="border border-zinc-700 text-zinc-300 px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2 hover:border-primary-accent hover:text-white disabled:opacity-50"
              >
                <RefreshCw size={12} className={refreshing ? "animate-spin" : ""}/> {refreshing ? "Syncing…" : "Sync Provider"}
              </button>
            </>
          )}
        </div>
      </div>

      {empty && (
        <div className="surface-card p-8 text-center">
          <AlertCircle size={32} className="mx-auto text-zinc-500 mb-3"/>
          <div className="font-display text-xl uppercase">No Standings Yet</div>
          <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
            Standings are computed from settled match results (Win = 3, Draw = 1; ranked by points, then goal difference, then goals for).
            {user?.role === "admin" && " Settle group matches under Admin → Matches to populate the tables."}
          </p>
        </div>
      )}

      {!empty && data.format === "grouped" && (
        <div className="flex flex-wrap gap-1 mb-4 surface-card p-1 inline-flex" data-testid="group-tabs">
          {groups.map((g) => (
            <button
              key={g}
              data-testid={`group-tab-${g}`}
              onClick={() => setGroupTab(g)}
              className={`px-4 py-1.5 text-xs uppercase tracking-widest ${
                groupTab === g ? "bg-primary-accent text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              Group {g}
            </button>
          ))}
        </div>
      )}

      {!empty && currentTable && (
        <div className="surface-card overflow-x-auto" data-testid={isLeague ? "league-table" : undefined}>
          {data.format === "grouped" ? (
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-display text-2xl uppercase">Group {currentTable.group}</h2>
                {currentTable.complete ? (
                  <span data-testid={`group-status-${currentTable.group}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-green-500/50 text-green-400 bg-green-500/10">
                    <Check size={11}/> Final
                  </span>
                ) : (
                  <span data-testid={`group-status-${currentTable.group}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest border border-yellow-400/40 text-yellow-300 bg-yellow-400/5">
                    Provisional · {currentTable.played_matches ?? 0}/{currentTable.total_matches ?? 0} played
                  </span>
                )}
              </div>
              <span className="label-tag">{currentTable.table?.length || 0} teams</span>
            </div>
          ) : isLeague ? (
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <h2 className="font-display text-2xl uppercase">League Table</h2>
              <span className="label-tag">{currentTable.table?.length || 0} teams</span>
            </div>
          ) : null}
          <StandingsTable rows={currentTable.table || []} testidPrefix={`tbl-${currentTable.group || "league"}`} clZone={clZone} relegationZone={relegationZone}/>
        </div>
      )}

      {!empty && data.format === "grouped" && data.best_thirds?.length > 0 && (
        <BestThirdsTable rows={data.best_thirds} />
      )}

      {!empty && isLeague && (clZone > 0 || relegationZone > 0) && (
        <div className="mt-4 flex flex-wrap items-center gap-5 text-xs text-zinc-400" data-testid="zone-legend">
          {clZone > 0 && <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-green-500"/> Champions League (top {clZone})</div>}
          {relegationZone > 0 && <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-primary-accent"/> Relegation (bottom {relegationZone})</div>}
        </div>
      )}

      {!empty && (
        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-zinc-500">
          <div className="flex items-center gap-2"><FormDot result="W"/> Win</div>
          <div className="flex items-center gap-2"><FormDot result="D"/> Draw</div>
          <div className="flex items-center gap-2"><FormDot result="L"/> Loss</div>
          <span className="text-zinc-600" data-testid="standings-source-note">Computed from settled results · Win=3, Draw=1 · ties broken by head-to-head, then GD, GF, GA · Form shows last 5</span>
        </div>
      )}
    </div>
  );
}
